import { useState, useRef, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link } from '@tanstack/react-router';
import { Monitor, LayoutDashboard, Globe } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { usePreferences } from '../../hooks/usePreferences';
import { useTranslation } from '../../i18n';
import type { Locale } from '../../i18n';
import { THEMES, THEME_DEFINITIONS } from '@siliconvault/shared';
import styles from './PublicShell.module.css';

interface PublicShellProps {
  children: ReactNode;
}

export function PublicShell({ children }: PublicShellProps) {
  const { isAuthenticated } = useAuth();
  const { preferences, setPreference } = usePreferences();
  const { t, locale } = useTranslation();
  const [themeMenuOpen, setThemeMenuOpen] = useState(false);
  const themeToggleRef = useRef<HTMLButtonElement>(null);
  const themeDropdownRef = useRef<HTMLDivElement>(null);
  const [themeMenuStyle, setThemeMenuStyle] = useState<React.CSSProperties>({});

  function toggleLocale() {
    const next: Locale = locale === 'en' ? 'he' : 'en';
    setPreference('locale', next);
  }

  const updateThemeMenuPos = useCallback(() => {
    if (themeToggleRef.current) {
      const rect = themeToggleRef.current.getBoundingClientRect();
      const isRtl = document.documentElement.dir === 'rtl';
      const menuWidth = 180;
      if (isRtl) {
        const left = Math.max(8, Math.min(rect.left, window.innerWidth - menuWidth - 8));
        setThemeMenuStyle({ top: rect.bottom + 6, left, right: 'auto' });
      } else {
        const right = Math.max(8, window.innerWidth - rect.right);
        setThemeMenuStyle({ top: rect.bottom + 6, right, left: 'auto' });
      }
    }
  }, []);

  useEffect(() => {
    if (!themeMenuOpen) return;
    updateThemeMenuPos();
    function handleClickOutside(e: MouseEvent) {
      if (
        themeDropdownRef.current && !themeDropdownRef.current.contains(e.target as Node) &&
        themeToggleRef.current && !themeToggleRef.current.contains(e.target as Node)
      ) {
        setThemeMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [themeMenuOpen, updateThemeMenuPos]);

  return (
    <div className={styles.shell}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <Link to="/" className={styles.logo}>
            <img src="/icon.svg" alt="" className={styles.logoIcon} />
            <span>SiliconVault</span>
          </Link>
        </div>

        <div className={styles.headerRight}>
          {/* Language toggle */}
          <button
            className={styles.langToggle}
            onClick={toggleLocale}
            title={locale === 'en' ? 'עברית' : 'English'}
          >
            <Globe size={14} strokeWidth={1.75} />
            <span>{locale === 'en' ? 'EN' : 'עב'}</span>
          </button>

          {/* Theme dropdown */}
          <button
            ref={themeToggleRef}
            className={styles.themeToggle}
            onClick={() => setThemeMenuOpen(!themeMenuOpen)}
            title={t('nav.switchTheme')}
          >
            <Monitor size={14} strokeWidth={1.75} />
            <span
              style={{
                position: 'absolute',
                bottom: 3,
                right: 3,
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: THEME_DEFINITIONS[preferences.theme]?.colors['--accent'] ?? '#00ff41',
              }}
            />
          </button>

          {themeMenuOpen && createPortal(
            <div ref={themeDropdownRef} className={styles.themeDropdown} style={themeMenuStyle}>
              {THEMES.map((id) => {
                const def = THEME_DEFINITIONS[id];
                return (
                  <button
                    key={id}
                    className={`${styles.themeDropdownItem} ${preferences.theme === id ? styles.themeDropdownItemActive : ''}`}
                    onClick={() => { setPreference('theme', id); setThemeMenuOpen(false); }}
                  >
                    <span
                      className={styles.themeDot}
                      style={{ background: def.colors['--bg-primary'], border: `2px solid ${def.colors['--accent']}` }}
                    />
                    {def.label}
                  </button>
                );
              })}
            </div>,
            document.body
          )}

          {isAuthenticated && (
            <Link to="/dashboard" className={styles.dashboardLink}>
              <LayoutDashboard size={14} strokeWidth={1.75} />
              {t('share.backToDashboard')}
            </Link>
          )}
        </div>
      </header>

      <main className={styles.main}>
        {children}
      </main>

      <footer className={styles.footer}>
        &copy; {new Date().getFullYear()} SiliconVault.{' '}
        <Link to="/terms" className={styles.footerLink}>{t('footer.terms')}</Link>
        <span className={styles.footerSep}> | </span>
        <Link to="/privacy" className={styles.footerLink}>{t('footer.privacy')}</Link>
      </footer>
    </div>
  );
}
